/**
 * Static region → ISO 3166-1 alpha-2 country code mapping.
 * Country lists are sorted by market research significance, then alphabetically.
 */

import { intlLocaleForUi } from '@/i18n'

export interface RegionDef {
  id: string
  label: string
  countries: Array<string>
}

export const REGIONS: Array<RegionDef> = [
  {
    id: 'north_central_america',
    label: 'North & Central America',
    countries: [
      // North America
      'US', 'CA', 'MX',
      // Central America
      'GT', 'CR', 'PA', 'HN', 'SV', 'NI', 'BZ',
      // Caribbean
      'CU', 'DO', 'JM', 'HT', 'TT', 'BB', 'BS', 'PR',
      'DM', 'GD', 'AG', 'KN', 'LC', 'VC',
    ],
  },
  {
    id: 'south_america',
    label: 'South America',
    countries: [
      'BR', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'BO', 'PY', 'UY',
      'GY', 'SR',
    ],
  },
  {
    id: 'europe',
    label: 'Europe',
    // Western Europe
    countries: [
      'GB', 'FR', 'DE', 'IT', 'ES', 'NL', 'SE', 'PL',
      'PT', 'BE', 'CH', 'AT', 'NO', 'DK', 'FI', 'IE',
      'LU',
      // Central & Eastern Europe
      'CZ', 'SK', 'HU', 'RO', 'BG', 'UA', 'BY', 'MD',
      // Southern Europe
      'GR', 'HR', 'SI', 'RS', 'BA', 'ME', 'MK', 'AL',
      // Baltics
      'LT', 'LV', 'EE',
      // Other
      'IS', 'MT', 'CY', 'LI',
      // Caucasus
      'TR', 'GE', 'AM', 'AZ',
    ],
  },
  {
    id: 'asia_pacific',
    label: 'Asia Pacific',
    // East Asia
    countries: [
      'JP', 'CN', 'KR', 'TW', 'HK', 'MO',
      // South Asia
      'IN', 'BD', 'PK', 'LK', 'NP',
      // South-East Asia
      'SG', 'ID', 'MY', 'TH', 'PH', 'VN', 'MM', 'KH', 'LA', 'BN',
      // Oceania
      'AU', 'NZ', 'FJ', 'PG',
      // Central Asia
      'KZ', 'UZ', 'TM', 'TJ', 'KG',
      // Other Asia
      'MN',
    ],
  },
  {
    id: 'middle_east_africa',
    label: 'Middle East & Africa',
    // Gulf & Levant
    countries: [
      'AE', 'SA', 'QA', 'KW', 'BH', 'OM',
      'IL', 'JO', 'LB', 'IQ', 'IR', 'YE', 'SY',
      // North Africa
      'EG', 'MA', 'TN', 'DZ', 'LY', 'SD',
      // Sub-Saharan Africa — West
      'NG', 'GH', 'SN', 'CI', 'CM', 'ML', 'BF', 'NE',
      'LR', 'SL', 'GN', 'GW', 'GM', 'CV', 'MR',
      // Sub-Saharan Africa — Central
      'CD', 'CG', 'CF', 'GA', 'GQ', 'ST', 'AO',
      // Sub-Saharan Africa — East
      'KE', 'TZ', 'ET', 'UG', 'RW', 'BI', 'DJ',
      'SO', 'ER', 'KM',
      // Sub-Saharan Africa — Southern
      'ZA', 'ZM', 'ZW', 'MZ', 'MG', 'BW', 'NA',
      'MW', 'LS', 'SZ', 'MU',
      'TD',
    ],
  },
]

const displayNamesByLocale = new Map<string, Intl.DisplayNames>()

function displayNamesFor(locale: string): Intl.DisplayNames {
  const resolved = intlLocaleForUi(locale)
  const cached = displayNamesByLocale.get(resolved)
  if (cached) {
    return cached
  }
  const created = new Intl.DisplayNames(resolved, { type: 'region' })
  displayNamesByLocale.set(resolved, created)
  return created
}

/** Resolve an ISO alpha-2 code to a human-readable country name in the UI locale. */
export function countryName(code: string, locale = 'en'): string {
  return displayNamesFor(locale).of(code) ?? code
}
